/**
 * Small DOM hooks used by FloatingPanel: media query, viewport size,
 * focus trap with restore, a ResizeObserver callback and the header bottom.
 */
import { useEffect, useRef, useState } from "react";

import { headerBottomOf, readViewport } from "./floatingGeometry";

const FOCUSABLE_SELECTOR = [
  "a[href]", "button:not([disabled])", "input:not([disabled])", "select:not([disabled])",
  "textarea:not([disabled])", "[tabindex]:not([tabindex='-1'])",
].join(",");

const matchQuery = (query) => {
  if (!query || typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(query).matches;
};

/** `query == null` always reads false and attaches nothing. */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => matchQuery(query));
  useEffect(() => {
    if (!query || typeof window === "undefined" || typeof window.matchMedia !== "function") {
      setMatches(false);
      return undefined;
    }
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

/** Live window size; only listens while `enabled`. */
export function useViewport(enabled) {
  const [viewport, setViewport] = useState(readViewport);
  useEffect(() => {
    if (!enabled) return undefined;
    const onResize = () => setViewport(readViewport());
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [enabled]);
  return viewport;
}

const focusableIn = (container) =>
  Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((el) => el.getAttribute("aria-hidden") !== "true");

/**
 * While `active`, Tab and Shift+Tab cycle inside the container. On activation
 * focus moves into it; on deactivation focus returns to whatever held it
 * before, when that element is still in the document.
 */
export function useFocusTrap(containerRef, active) {
  useEffect(() => {
    const container = containerRef.current;
    if (!active || !container) return undefined;
    const previous = document.activeElement;
    if (!container.contains(document.activeElement)) {
      (focusableIn(container)[0] || container).focus();
    }
    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const list = focusableIn(container);
      if (!list.length) { e.preventDefault(); container.focus(); return; }
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === container)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener("keydown", onKeyDown);
    return () => {
      container.removeEventListener("keydown", onKeyDown);
      if (previous && typeof previous.focus === "function" && document.contains(previous)) {
        previous.focus();
      }
    };
  }, [containerRef, active]);
}

/** Calls `onResize({width, height})` whenever the observed element changes size. */
export function useResizeCallback(elementRef, onResize, enabled) {
  const callbackRef = useRef(onResize);
  callbackRef.current = onResize;
  useEffect(() => {
    const el = elementRef.current;
    if (!enabled || !el || !callbackRef.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box) callbackRef.current?.({ width: box.width, height: box.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [elementRef, enabled]);
}

/**
 * Bottom edge (viewport px) of the elements matching `selector` (the app's
 * header bars), 0 when none is laid out. Re-measured on window resize, when
 * any matched element changes size (ResizeObserver) and when `remeasureKey`
 * changes (the panel opening is a cheap moment to pick up a re-mounted header).
 */
export function useHeaderBottom(selector, enabled, remeasureKey) {
  const [bottom, setBottom] = useState(0);
  useEffect(() => {
    if (!enabled || !selector || typeof document === "undefined") {
      setBottom(0);
      return undefined;
    }
    const elements = Array.from(document.querySelectorAll(selector));
    const measure = () => setBottom(headerBottomOf(elements.map((el) => el.getBoundingClientRect())));
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    elements.forEach((el) => observer?.observe(el));
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [selector, enabled, remeasureKey]);
  return bottom;
}
